/* eslint-disable */
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { CONSTANTS } from './config.service';
import { DateTimeService } from './date-time/date-time.service';
import { LoggerService } from './logger.service';
import { PersonsService } from './persons.service';
import { SqlHelperService } from './sql-helper.service';
import { StudyService } from './study.service';
import { SchemaService } from './survey/schema.service';
import { UtilsService } from './utils.service';
import { MSRemoteLoggerCategory } from 'src/app/adapters/remote-logger/utils/remote-logger.utils';

@Injectable({
  providedIn: 'root'
})
export class DailySurveyService {
  isLd = false;
  db;
  table = CONSTANTS.dailySurveyTableName;
  noUpdateFields = ['id', 'firstCompleted', 'lastCompleted', 'completedCount', 'lap', 'maxLapIndex'];
  createTableSql =
    'CREATE TABLE IF NOT EXISTS ' +
    this.table +
    ' ( ' +
    'id INTEGER primary key autoincrement, ' +
    'firstStarted INTEGER, ' +
    'lastStarted INTEGER, ' +
    'firstCompleted INTEGER, ' +
    'lastCompleted INTEGER, ' +
    'completedCount INTEGER DEFAULT 0, ' +
    'queued INTEGER DEFAULT 0, ' + // bool
    'deleteFlag INTEGER DEFAULT NULL, ' + // bool, 'delete' is a js keyword, this col is for the server
    'hideFromUi INTEGER DEFAULT 0, ' + // bool
    'surveyDate INTEGER, ' +
    'complete INTEGER DEFAULT 0, ' +
    'lastPage TEXT, ' +
    'takerPersonId INTEGER, ' +
    'subjectPersonId INTEGER, ' +
    'callCenterAnswered INTEGER DEFAULT 0, ' +
    'path TEXT, ' +
    'futurePath TEXT, ';

  constructor(
    private logger: LoggerService,
    private sqlHelper: SqlHelperService,
    //private prefs: PrefsProvider,
    private utils: UtilsService,
    private schema: SchemaService,
    private study: StudyService,
    private dateTime: DateTimeService,
    private persons: PersonsService
  ) {
    this.logger.log('Daily survey constructor', MSRemoteLoggerCategory.DAILY_SURVEY_SERVICE);
  }

  /************************************************************************************
   * Counting
   ***********************************************************************************/

  // used for badge and status counting
  countUnfinished() {
    return this.sqlHelper.getCountPromise(this.db, this.table, { complete: 0, hideFromUi: 0 });
  }

  countUnfinishedByPersonId(pId) {
    return this.sqlHelper.getCountPromise(this.db, this.table, { complete: 0, hideFromUi: 0, subjectPersonId: pId });
  }

  getCountDetails() {
    return this.sqlHelper.getSelectPromise(this.db, this.table, ['complete', 'hideFromUi', 'surveyDate']);
  }

  /************************************************************************************
   * Deleting
   ***********************************************************************************/
  delete(surveyId) {
    const updates = { deleteFlag: 1, hideFromUi: 1, queued: 0 };
    return this.sqlHelper.getUpdatePromise(this.db, this.table, updates, { id: surveyId });
  }

  /************************************************************************************
   * Inserting
   ***********************************************************************************/

  insert(ymd, subjectPersonId) {
    return this.sqlHelper.getInsertPromise(this.db, this.table, {
      surveyDate: ymd,
      subjectPersonId,
      firstStarted: this.dateTime.getNowSeconds()
    });
  }

  setSurveys(dailies, trips, person) {
    const promises = [];
    const phases = this.study.phases;
    const ISSUANCE = CONSTANTS.DAILY_SURVEY_ISSUANCE;
    const today = moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    const yesterday = today.subtract(1, 'day');

    phases.forEach((phase) => {
      if (phase.summaryIssuance == ISSUANCE.NEVER) {
        return;
      }

      const issueFrom = moment(phase.start, 'YYYYMMDD');
      const issueTo = moment.min(yesterday, moment(phase.stop, 'YYYYMMDD'));

      while (issueFrom.isSameOrBefore(issueTo)) {
        const ymd = issueFrom.format('YYYYMMDD');
        const hasSurveyThisDay = dailies.filter((d) => d.surveyDate == ymd)[0];

        // if issuance = everyday, issue if it doesn't exist
        if (phase.summaryIssuance === ISSUANCE.EVERYDAY && !hasSurveyThisDay) {
          promises.push(this.insert(ymd, person.idx));

          // if issuance = trips, issue if doesn't exist and should, or delete if exists and shouldn't
        } else if (phase.summaryIssuance === ISSUANCE.TRIPS) {
          const hasTripsThisDay = trips.filter((t) => t.ymd === ymd).length > 0;

          if (hasTripsThisDay && !hasSurveyThisDay) {
            promises.push(this.insert(ymd, person.idx));
          }
          if (!hasTripsThisDay && hasSurveyThisDay) {
            promises.push(this.delete(hasSurveyThisDay.id));
          }
        }
        issueFrom.add(1, 'day');
      }
    });

    return Promise.all(promises);
  }

  /************************************************************************************
   * Updating
   ***********************************************************************************/

  update(survey) {
    const updateValues = Object.assign({}, survey);

    this.noUpdateFields.forEach((field) => {
      delete updateValues[field];
    });

    if (survey.complete === 1) {
      if (survey.firstCompleted === 0 || survey.firstCompleted === null) {
        updateValues.firstCompleted = this.dateTime.getNowSeconds();
      }
      updateValues.lastCompleted = this.dateTime.getNowSeconds();
      updateValues.completedCount_LITERAL = 'completedCount + 1';
    }

    return this.sqlHelper.getUpdatePromise(this.db, this.table, updateValues, { id: survey.id }).then((updateCount) => updateCount);
  }

  updateSurveyNav(survey) {
    // ignores other fields and just updates nav fields (for previous)
    const updateValues = this.utils.subset(survey, ['path', 'lastPage']);
    return this.sqlHelper.getUpdatePromise(this.db, this.table, updateValues, { id: survey.id });
  }

  /************************************************************************************
   * Getting
   ***********************************************************************************/

  get(id) {
    return this.sqlHelper.getSelectPromise(this.db, this.table, ['*'], { id }).then((rows) => rows[0]);
  }

  getByPersonId(pId) {
    return this.sqlHelper.getSelectPromise(this.db, this.table, ['*'], { subjectPersonId: pId }).then((rows) => rows[0]);
  }

  getAll() {
    return this.sqlHelper.getSelectPromise(this.db, this.table, ['*'], { hideFromUi: 0 }, 'surveyDate DESC');
  }

  getAllForRoster(trips, person, isLd) {
    return this.getAll()
      .then((res: any[]) => this.setSurveys(res, trips, person))
      .then(() => this.getAll());
  }

  /************************************************************************************
   * Queueing
   ***********************************************************************************/

  markQueued(id) {
    return this.sqlHelper.getUpdatePromise(this.db, this.table, { queued: 1 }, { id });
  }

  getUnqueuedIds() {
    return this.sqlHelper.getSelectPromise(this.db, this.table, ['id'], { queued: 0 });
  }

  hasUnqueued() {
    // including unfinished
    return this.sqlHelper.getExistsPromise(this.db, this.table, { queued: 0 });
  }

  /************************************************************************************
   * Initializing
   ***********************************************************************************/

  getCreateTableSql(isLongDistance) {
    const dbColDefs = [];
    // TODO? dailies don't currently support roster fields
    return this.schema.parse(CONSTANTS.surveyTypes.DAILY).then((fields) => {
      fields.forEach(function (field) {
        const name = field[0];
        const type = field[1];
        dbColDefs.push(name + ' ' + type);
      });
      return this.createTableSql + dbColDefs.join(', ') + ');';
    });
  }

  initialize(isLongDistance) {
    this.isLd = isLongDistance;

    return (
      this.sqlHelper
        .createDb({ name: CONSTANTS.mainFrontEndDbName, location: 'default', androidDatabaseProvider: 'system', androidLockWorkaround: 1 })
        .then((db) => {
          this.db = db;
          return this.getCreateTableSql(isLongDistance);
        })
        // .then(res => {
        //     if (CONSTANTS.SETTINGS.environment === 'dev') {
        //         return this.sqlHelper.createTablePromise(this.db, 'DROP TABLE IF EXISTS day')
        //             .then(() => {
        //                 return this.sqlHelper.createTablePromise(this.db, res);
        //             })
        //     } else {
        //         return this.sqlHelper.createTablePromise(this.db, res);
        //     }
        // })
        .then((res) => this.sqlHelper.createTablePromise(this.db, res))
        .catch((err) => {
          this.logger.log('Error initializing daily survey provider', MSRemoteLoggerCategory.DAILY_SURVEY_SERVICE, err);
        })
    );
  }
}
